/**
 * BusinessSection — the store's own identity, as it prints on a tax invoice.
 *
 * Every field here is already read by the receipt builder and the A4 invoice
 * (see ReceiptPreview: name, address, city, state, pincode, gstinOverride,
 * phone). Until now none of them could be edited from the app, so a merchant
 * could only get their own address onto their own bill by having it set
 * server-side. Rule 46 requires the supplier's name, address and GSTIN on a tax
 * invoice, which made this a compliance gap rather than a missing convenience.
 *
 * Writes go to the local `stores` record with a version bump and `isSynced`
 * cleared, exactly like the account form — so an edit made offline reaches the
 * server on the next sync rather than being lost.
 */
import { notifySuccess } from '@/store/useToastStore';
import { useErrorPopup } from '@/hooks/useErrorPopup';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Q } from '@nozbe/watermelondb';
import { Store as StoreIcon, Check, ImagePlus, Trash2 } from 'lucide-react';
import Store from '@/db/models/Store';
import Merchant from '@/db/models/Merchant';
import { database } from '@/db';
import { useAppStore } from '@/store/useAppStore';
import { useSettingsStore } from '@/store/useSettingsStore';
import { BANK_SETTING_KEYS, isPlausibleIfsc } from '@/printing/bankDetails';
import { upsertSetting } from './settingsWrite';
import {
  LOGO_ACCEPT,
  LOGO_MAX_BYTES,
  LogoError,
  describeBytes,
  prepareLogo,
} from '@/printing/logoImage';
import { useObservable } from '@/hooks/useObservable';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Explain, ExplainRow } from '@/components/ui/Explain';
import { Code } from '@/components/ui/numeric';
// The shared GST package is the single source of truth for both of these —
// a second copy of the GSTIN pattern here is exactly how the three clients
// drifted apart before ([[gst-core-shared-package]]).
import { GST_STATE_CODES, GSTIN_REGEX } from '@/reports/gst/gstStates';
import { cn } from '@/utils/cn';

/** [code, name] pairs, sorted by name for the picker. */
const STATES = Object.entries(GST_STATE_CODES).sort((a, b) => a[1].localeCompare(b[1]));

function txn<T>(fn: (db: typeof database) => Promise<T>): Promise<T> {
  return database.write(() => fn(database));
}

export function BusinessSection() {
  const activeStoreId = useAppStore((s) => s.activeStoreId);

  const stores = useObservable<Store[]>(
    () =>
      activeStoreId
        ? database.get<Store>('stores').query(Q.where('id', activeStoreId)).observe()
        : null,
    [activeStoreId],
    [],
  );
  const store = stores[0] ?? null;

  // The bill's GSTIN is the store override OR the merchant's — loadStoreGstIdentity
  // resolves it that way, and the printed invoice follows. Without the merchant
  // here, this preview warned "No GSTIN — bills will print as a Bill of Supply"
  // at a GST-registered shop that had simply left the store override blank,
  // which is the normal setup for a single outlet.
  const merchants = useObservable<Merchant[]>(
    () =>
      store?.merchantId
        ? database
            .get<Merchant>('merchants')
            .query(Q.where('id', store.merchantId))
            // observeWithColumns, not observe: a plain observe emits when the
            // set of matching RECORDS changes, not when a column on one of
            // them does. Removing the logo left the preview showing it.
            .observeWithColumns(['logo_url', 'gstin'])
        : null,
    [store?.merchantId],
    [],
  );
  const merchant = merchants[0] ?? null;

  const [name, setName] = useState('');
  const [gstin, setGstin] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [stateName, setStateName] = useState('');
  const [pincode, setPincode] = useState('');
  const [phone, setPhone] = useState('');
  const [upiId, setUpiId] = useState('');

  // Bank details live in settings rather than on the store row — see the note
  // in printing/bankDetails.ts for why.
  const [bankName, setBankName] = useState(() => useSettingsStore.getState().getSetting(BANK_SETTING_KEYS.name) ?? '');
  const [bankAccountNo, setBankAccountNo] = useState(() => useSettingsStore.getState().getSetting(BANK_SETTING_KEYS.accountNo) ?? '');
  const [bankIfsc, setBankIfsc] = useState(() => useSettingsStore.getState().getSetting(BANK_SETTING_KEYS.ifsc) ?? '');
  const [bankBranch, setBankBranch] = useState(() => useSettingsStore.getState().getSetting(BANK_SETTING_KEYS.branch) ?? '');

  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useErrorPopup(error, () => setError(null));

  // Hydrate once the store record arrives, and again if it changes underneath
  // us (a sync pull can rewrite it).
  useEffect(() => {
    if (!store) return;
    setName(store.name ?? '');
    setGstin(store.gstinOverride ?? '');
    setAddress(store.address ?? '');
    setCity(store.city ?? '');
    setStateName(store.state ?? '');
    setPincode(store.pincode ?? '');
    setPhone(store.phone ?? '');
    setUpiId(store.upiId ?? '');
  }, [store?.id, store?.updatedAt?.getTime()]);

  const gstinTrimmed = gstin.trim().toUpperCase();
  const gstinValid = gstinTrimmed === '' || GSTIN_REGEX.test(gstinTrimmed);

  /**
   * The state code embedded in the GSTIN. Place of supply is derived from it,
   * so a GSTIN whose first two digits disagree with the selected state is the
   * difference between charging CGST+SGST and IGST — worth surfacing here
   * rather than discovering it on a filed return.
   */
  const gstinStateMismatch = useMemo(() => {
    if (!gstinValid || gstinTrimmed.length < 2 || !stateName) return null;
    const fromGstin = GST_STATE_CODES[gstinTrimmed.slice(0, 2)];
    if (!fromGstin) return null;
    return fromGstin.toLowerCase() === stateName.toLowerCase() ? null : fromGstin;
  }, [gstinTrimmed, gstinValid, stateName]);

  // Store override first, business GSTIN second — the same precedence the
  // print pipeline applies.
  const effectiveGstin = gstinTrimmed || (merchant?.gstin ?? '').trim();

  const dirty =
    !!store &&
    (name !== (store.name ?? '') ||
      gstinTrimmed !== (store.gstinOverride ?? '') ||
      address !== (store.address ?? '') ||
      city !== (store.city ?? '') ||
      stateName !== (store.state ?? '') ||
      pincode !== (store.pincode ?? '') ||
      phone !== (store.phone ?? '') ||
      upiId !== (store.upiId ?? '') ||
      bankName !== (useSettingsStore.getState().getSetting(BANK_SETTING_KEYS.name) ?? '') ||
      bankAccountNo !== (useSettingsStore.getState().getSetting(BANK_SETTING_KEYS.accountNo) ?? '') ||
      bankIfsc !== (useSettingsStore.getState().getSetting(BANK_SETTING_KEYS.ifsc) ?? '') ||
      bankBranch !== (useSettingsStore.getState().getSetting(BANK_SETTING_KEYS.branch) ?? ''));

  // ── Logo ────────────────────────────────────────────────────────────────
  // Saved on pick rather than with the form. It is a single value with no
  // validation to co-ordinate, and a merchant who chooses a file expects it to
  // be their logo — not to be told later that they also had to press Save.
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  useErrorPopup(logoError, () => setLogoError(null));
  const [logoNote, setLogoNote] = useState<string | null>(null);

  async function writeLogo(value: string | null) {
    if (!merchant) throw new LogoError('No business record found for this store.');
    await txn(async () => {
      await merchant.update((record) => {
        record.logoUrl = value;
        record.version = (record.version ?? 1) + 1;
        record.isSynced = false;
      });
    });
  }

  async function onPickLogo(file: File) {
    setLogoError(null);
    setLogoNote(null);
    setLogoBusy(true);
    try {
      const prepared = await prepareLogo(file);
      await writeLogo(prepared.dataUrl);
      setLogoNote(
        `Saved at ${prepared.width}×${prepared.height}, ${describeBytes(prepared.bytes)}` +
          (prepared.lossy ? ' — compressed to fit.' : '.'),
      );
    } catch (err) {
      setLogoError(
        err instanceof LogoError ? err.message : 'That image could not be used. Try another file.',
      );
    } finally {
      setLogoBusy(false);
      // Let the same file be picked again after a failure.
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  }

  async function onRemoveLogo() {
    setLogoError(null);
    setLogoNote(null);
    try {
      await writeLogo(null);
      setLogoNote('Logo removed.');
    } catch (err) {
      setLogoError(
        err instanceof LogoError ? err.message : 'Could not remove the logo. Try again.',
      );
    }
  }

  async function save() {
    if (!store) return;
    setError(null);
    setSaved(false);

    if (!name.trim()) {
      setError('Store name is required — it is the supplier name on every invoice.');
      return;
    }
    if (!gstinValid) {
      setError('That GSTIN is not a valid 15-character number. Leave it blank if the store is unregistered.');
      return;
    }
    if (pincode.trim() && !/^\d{6}$/.test(pincode.trim())) {
      setError('PIN code must be 6 digits.');
      return;
    }

    await txn(async () => {
      await store.update((record) => {
        record.name = name.trim();
        record.gstinOverride = gstinTrimmed || null;
        record.address = address.trim() || null;
        record.city = city.trim() || null;
        record.state = stateName.trim() || null;
        record.pincode = pincode.trim() || null;
        record.phone = phone.trim() || null;
        record.upiId = upiId.trim() || null;
        record.version = store.version + 1;
        record.isSynced = false;
      });

      const merchantId = store.merchantId;
      if (merchantId) {
        await upsertSetting({ merchantId, key: BANK_SETTING_KEYS.name, value: bankName.trim() });
        await upsertSetting({ merchantId, key: BANK_SETTING_KEYS.accountNo, value: bankAccountNo.trim() });
        await upsertSetting({ merchantId, key: BANK_SETTING_KEYS.ifsc, value: bankIfsc.trim().toUpperCase() });
        await upsertSetting({ merchantId, key: BANK_SETTING_KEYS.branch, value: bankBranch.trim() });
      }
    });
    setSaved(true);
    notifySuccess('Business details saved — they will sync to your other devices.');
  }

  if (!store) {
    return (
      <Card className="p-5">
        <p className="text-body text-fg-secondary">No active store. Pick a store first.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-5">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-heading font-medium text-fg-primary">Business information</h2>
            <p className="text-label text-fg-secondary">
              This is what prints at the top of every bill and tax invoice.
            </p>
          </div>
          <Explain
            title="Where this appears"
            className="shrink-0 text-label text-fg-tertiary"
            content={
              <>
                <p>
                  Name, address and GSTIN are the supplier block on a tax invoice — Rule 46
                  requires all three on a registered sale.
                </p>
                <ExplainRow label="Thermal receipt" value="Header" />
                <ExplainRow label="A4 tax invoice" value="Supplier block" />
                <ExplainRow label="GSTR-1" value="From GSTIN" />
              </>
            }
          >
            Where does this print?
          </Explain>
        </div>

        <div className="grid max-w-3xl grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="sm:col-span-2">
            <span className="mb-1 block text-label uppercase text-fg-tertiary">
              Store name <span className="text-danger-ink">*</span>
            </span>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Big Mart Supermarket" />
          </label>

          <label className="sm:col-span-2">
            <span className="mb-1 block text-label uppercase text-fg-tertiary">GSTIN</span>
            <Input
              value={gstin}
              onChange={(e) => setGstin(e.target.value.toUpperCase().replace(/\s/g, '').slice(0, 15))}
              placeholder="33ABCDE1234F1Z5"
              className={cn(!gstinValid && 'border-danger')}
            />
            {!gstinValid ? (
              <span className="mt-1 block text-label text-danger-ink">
                Not a valid 15-character GSTIN.
              </span>
            ) : gstinStateMismatch ? (
              <span className="mt-1 block text-label text-warning-ink">
                This GSTIN is registered in {gstinStateMismatch}, but the store state says{' '}
                {stateName}. Place of supply is derived from the GSTIN, so this decides
                CGST+SGST vs IGST.
              </span>
            ) : (
              <span className="mt-1 block text-label text-fg-tertiary">
                Leave blank if the store is not GST-registered.
              </span>
            )}
          </label>

          <label className="sm:col-span-2">
            <span className="mb-1 block text-label uppercase text-fg-tertiary">Address</span>
            <Input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="No. 12, 1st Main Road, Anna Nagar"
            />
          </label>

          <label>
            <span className="mb-1 block text-label uppercase text-fg-tertiary">City</span>
            <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Chennai" />
          </label>

          <label>
            <span className="mb-1 block text-label uppercase text-fg-tertiary">PIN code</span>
            <Input
              value={pincode}
              onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              placeholder="600040"
            />
          </label>

          <label>
            <span className="mb-1 block text-label uppercase text-fg-tertiary">State</span>
            <select
              value={stateName}
              onChange={(e) => setStateName(e.target.value)}
              className="h-9 w-full rounded-md border border-stroke-hairline/15 bg-bg-surface px-3 text-body text-fg-primary"
            >
              <option value="">Select a state</option>
              {STATES.map(([code, stateLabel]) => (
                <option key={code} value={stateLabel}>
                  {stateLabel}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span className="mb-1 block text-label uppercase text-fg-tertiary">Phone</span>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98765 43210" />
          </label>

          <label className="sm:col-span-2">
            <span className="mb-1 block text-label uppercase text-fg-tertiary">UPI ID</span>
            <Input value={upiId} onChange={(e) => setUpiId(e.target.value)} placeholder="bigmart@upi" />
            <span className="mt-1 block text-label text-fg-tertiary">
              Printed as a QR on the receipt so a customer can pay from the bill.
            </span>
          </label>

          {/* ── Logo ─────────────────────────────────────────────────────
              Kept beside the rest of the supplier identity, because that is
              what it is: the mark at the top of the same block. */}
          <div className="sm:col-span-2 mt-2 border-t border-stroke-hairline/10 pt-4">
            <h4 className="text-body font-medium text-fg-primary">Logo</h4>
            <p className="mt-1 text-label text-fg-tertiary">
              Printed above your store name on the bill. Whatever you upload is resized and
              stored with the shop, so it prints even with no internet.
            </p>
          </div>

          <div className="sm:col-span-2 flex flex-wrap items-center gap-4">
            <div className="flex h-20 w-32 shrink-0 items-center justify-center overflow-hidden rounded-md border border-stroke-hairline/15 bg-white">
              {merchant?.logoUrl ? (
                <img
                  src={merchant.logoUrl}
                  alt="Your logo"
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <span className="text-label text-fg-tertiary">No logo</span>
              )}
            </div>

            <div className="min-w-0 space-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <input
                  ref={logoInputRef}
                  type="file"
                  accept={LOGO_ACCEPT}
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void onPickLogo(file);
                  }}
                />
                <Button
                  variant="secondary"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={logoBusy || !merchant}
                >
                  <ImagePlus size={14} className="mr-1.5" />
                  {logoBusy ? 'Working…' : merchant?.logoUrl ? 'Replace' : 'Upload a logo'}
                </Button>
                {merchant?.logoUrl && (
                  <button
                    type="button"
                    onClick={() => void onRemoveLogo()}
                    className="inline-flex items-center gap-1 text-label text-fg-tertiary transition-colors hover:text-danger-ink"
                  >
                    <Trash2 size={12} />
                    Remove
                  </button>
                )}
              </div>

              {logoNote ? (
                <p className="text-label text-success-ink">{logoNote}</p>
              ) : (
                <p className="text-label text-fg-tertiary">
                  PNG, JPG, WebP or SVG. It is shrunk to fit under {describeBytes(LOGO_MAX_BYTES)},
                  so a photo of your signboard is fine.
                </p>
              )}
            </div>
          </div>

          {/* ── Bank details ─────────────────────────────────────────────
              A buyer paying by transfer needs these on the invoice itself.
              Only the ruled A4 layout prints the block, so the hint says so
              rather than leaving someone to wonder why nothing appeared. */}
          <div className="sm:col-span-2 mt-2 border-t border-stroke-hairline/10 pt-4">
            <h4 className="text-body font-medium text-fg-primary">Bank details</h4>
            <p className="mt-1 text-label text-fg-tertiary">
              Printed on the invoice so a buyer can pay you by transfer. Used by the ruled A4
              layout; leave blank to keep the block off the bill entirely.
            </p>
          </div>

          <label>
            <span className="mb-1 block text-label uppercase text-fg-tertiary">Bank name</span>
            <Input value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="Indian Bank" />
          </label>

          <label>
            <span className="mb-1 block text-label uppercase text-fg-tertiary">Account number</span>
            <Input
              value={bankAccountNo}
              onChange={(e) => setBankAccountNo(e.target.value)}
              placeholder="6784449110"
              inputMode="numeric"
            />
          </label>

          <label>
            <span className="mb-1 block text-label uppercase text-fg-tertiary">IFSC code</span>
            <Input
              value={bankIfsc}
              onChange={(e) => setBankIfsc(e.target.value.toUpperCase())}
              placeholder="IDIB000K144"
              spellCheck={false}
            />
            {bankIfsc.trim() && !isPlausibleIfsc(bankIfsc) && (
              <span className="mt-1 block text-label text-warning-ink">
                That does not look like an IFSC (4 letters, 0, then 6 characters). Saved anyway —
                check it against your passbook.
              </span>
            )}
          </label>

          <label>
            <span className="mb-1 block text-label uppercase text-fg-tertiary">Branch</span>
            <Input value={bankBranch} onChange={(e) => setBankBranch(e.target.value)} placeholder="Kumbakonam Bazaar" />
          </label>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <Button onClick={() => void save()} disabled={!dirty}>
            Save business details
          </Button>
        </div>
      </Card>

      {/* What the bill will actually say. Editing an address on a form and
          hoping is not the same as seeing the header it produces. */}
      <Card className="p-5">
        <div className="mb-3 flex items-center gap-2">
          <StoreIcon size={16} className="text-fg-secondary" />
          <h2 className="text-heading font-medium text-fg-primary">Invoice header preview</h2>
        </div>
        <div className="rounded-md border border-stroke-hairline/15 bg-bg-subtle p-4">
          <div className="mx-auto max-w-sm text-center">
            <div className="text-body font-semibold uppercase text-fg-primary">
              {name.trim() || 'Your store name'}
            </div>
            {(address.trim() || city.trim() || pincode.trim()) && (
              <div className="mt-0.5 text-label text-fg-secondary">
                {[address.trim(), city.trim(), pincode.trim()].filter(Boolean).join(', ')}
              </div>
            )}
            {stateName && <div className="text-label text-fg-secondary">{stateName}</div>}
            {phone.trim() && <div className="text-label text-fg-secondary">Ph: {phone.trim()}</div>}
            {effectiveGstin && (
              <div className="mt-1 text-label text-fg-primary">
                GSTIN: <Code>{effectiveGstin}</Code>
                {!gstinTrimmed && (
                  <span className="ml-1 text-fg-tertiary">(from your business)</span>
                )}
              </div>
            )}
            {!effectiveGstin && (
              <div className="mt-1 text-label text-fg-tertiary">
                No GSTIN — bills will print as a Bill of Supply, not a tax invoice.
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
